import { Box, Button, Grid, Typography } from "@mui/material";
import {
  _ACTIONS,
  _COLORS,
  _MESSAGES,
  _STATUTCOLORS,
  _SWAL_ICONS,
} from "../../utils/_constants";
import MandatsFilterForm from "../forms/MandatFilerForm";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { btn, _STYLES } from "../../utils/_style_constants.js";
import { useContext, useEffect, useRef, useState } from "react";
import { DataGrid, frFR } from "@mui/x-data-grid";
import { RequestHelper } from "../../utils/requestHelper";
import { createToast } from "../../utils/toast";
import { useNavigate } from "react-router";
import Swal from "sweetalert2";
import { UserContext } from "../contexts/UserContext";
import moment from "moment";
import IpIsoleeForm from "../forms/IpIsoleeForm";
import BasicModal from "../helpers/BasicModal";
import MandatAction from "../helpers/MandatAction";
import { capitalizeFirstLetter } from "../../utils/helper";
import _ from "lodash";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as XLSX from "xlsx";
import { formatStringToNumber } from "../helpers/Utils.jsx";

/**
/**
 * This file contains the ListeMandats component, which displays a table of mandats and a filter form.
 * 
 * @module ListMandats
 * @returns {JSX.Element} The ListeMandats component.
 */
function ListeMandats(props) {
  const [rowsMandats, setRowsMandats] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const gridRef = useRef(null);
  const { user } = useContext(UserContext);
  const [agences, setAgences] = useState([]);
  const [refresh, setRefresh] = useState(0);
  const requestHelper = new RequestHelper();

  let navigate = useNavigate();

  /** Export excel */

  const handleExportToExcel = async () => {
    try {
      // Récupérer toutes les données depuis l'API
      const response = await requestHelper.get("/api/mandats");

      if (response.status === 200) {
        const allData = response.data["hydra:member"];

        // Préparer les données pour l'export avec toutes les informations
        const exportData = allData.map((row) => ({
          "# Dossier": row.numeroDossier || "N/A",
          "# Mandat": row.numero
            ? row.agence?.numeroAgence +
              (row.typeVente?.nomTechnique || "RE") +
              row.numero
            : "N/A",
          "Type de mandat": capitalizeFirstLetter(row.type),
          "Type de vente": row.typeVente?.nom || "N/A",
          Agence: capitalizeFirstLetter(row.agence?.nom),
          Mandants: row.mandants
            ? row.mandants
                .map((m) => (m.nom ? `${m.nom} ${m.prenom}` : ""))
                .filter(Boolean)
                .join(", ")
            : "N/A",
          Statut: row.statut?.nom || "N/A",
          "Type de bien": row.typeBien?.nom || "N/A",
          Exclusif:
            row.exclusif === null
              ? "Non renseigné"
              : row.exclusif
                ? "Oui"
                : "Non",
          "Adresse du bien": row.bien?.adresse || "N/A",
          Surface: row.bien?.surface || "N/A",
          "Valeur Vénale":
            formatStringToNumber(row.bien?.valeurBiens?.[0]?.valeurVenale) ||
            "N/A",
          Honoraires: row.remuneration || "N/A",
          "Honoraires à la charge de": row.remunerationPayeur || "N/A",
          "Lieu de vente": row.lieuDeVente || "N/A",
          "Date de création": row.dateCreation
            ? moment(row.dateCreation).format("DD/MM/YYYY")
            : "N/A",
          "Date de signature": row.dateSignature
            ? moment(row.dateSignature).format("DD/MM/YYYY")
            : "N/A",
        }));

        // Créer le workbook et la feuille
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Mandats");

        // Ajuster la largeur des colonnes
        const colWidths = Object.keys(exportData[0] || {}).map(() => ({
          wch: 20,
        }));
        ws["!cols"] = colWidths;

        // Télécharger le fichier
        XLSX.writeFile(
          wb,
          `mandats_complet_${moment().format("YYYYMMDD_HHmmss")}.xlsx`,
        );

        createToast(_SWAL_ICONS.success, "Export Excel réussi");
      } else {
        createToast(
          _SWAL_ICONS.error,
          "Erreur lors de la récupération des données",
        );
      }
    } catch (error) {
      console.error("Erreur lors de l'export:", error);
      createToast(_SWAL_ICONS.error, "Erreur lors de l'export Excel");
    }
  };
  /** récupére les données */

  useEffect(() => {
    if (!_.isEmpty(user)) {
      let agencies = {};
      if (user.profil === "SUSER" || user.profil === "USER") {
        user.agences.forEach((element) => {
          agencies[element.nom] = element.id;
        });
      } else {
        requestHelper.get("/api/agences").then((response) => {
          if (response.status === 200) {
            response.data["hydra:member"].forEach((element) => {
              agencies[element.nom] = element.id;
            });
          }
        });
      }
      setAgences(agencies);
    }
  }, [user]);

  /**
   * Handler for showing/hiding the modal for adding/editing an agency.
   */
  function showModalHandler() {
    setShowModal(!showModal);
  }

  const refreshGrid = () => {
    setRefresh(refresh + 1);
  };

  useEffect(() => {
    refreshGrid();
  }, [props.clickedChoice, user, agences]);

  const createAgenceOptions = (agences) => {
    let options = {};
    for (const p in agences) {
      options[agences[p]] = p;
    }

    return options;
  };

  /**
   * Columns for the DataGrid component.
   */
  const columnsListMandats = [
    {
      field: "numeroDossier",
      headerName: "# Dossier",
      flex: 1,
      headerClassName: "primaryLight",
      renderCell: (params) => {
        return (
          <strong>
            <span style={{ color: _COLORS.success }}>
              {params.row.numeroDossier ? params.row.numeroDossier : "N/A"}
            </span>
          </strong>
        );
      },
    },
    {
      field: "numeroMandat",
      headerName: "# Mandat",
      flex: 1,
      headerClassName: "primaryLight",
      valueGetter: (_value, row) => {
        if (!_value?.row.numero) return "N/A";
        const nPartenaire =
          _value.row.partenaireNumber !== null &&
          _value.row.partenaireNumber !== ""
            ? "P" + _value.row.partenaireNumber + "-"
            : "";
        return (
          nPartenaire +
          _value?.row?.agence?.numeroAgence +
          (_value?.row?.typeVente?.nomTechnique
            ? _value?.row?.typeVente?.nomTechnique
            : "RE") +
          _value?.row?.numero
        );
      },
      renderCell: (params) => {
        return (
          <strong>
            <span style={{ color: _COLORS.success }}>{params.value}</span>
          </strong>
        );
      },
    },
    {
      field: "type",
      headerName: "Type de mandat",
      flex: 1,
      headerClassName: "primaryLight",
      renderCell: (params) => {
        return (
          <Typography sx={{ fontWeight: "bold" }}>
            {capitalizeFirstLetter(params.row.type)}
          </Typography>
        );
      },
    },
    {
      field: "agence",
      headerName: "Agence",
      flex: 1,
      headerClassName: "primaryLight",
      renderCell: (params) => {
        return (
          <Typography sx={{ fontWeight: "bold" }}>
            {capitalizeFirstLetter(params.row.agence.nom)}
          </Typography>
        );
      },
    },
    {
      field: "mandants",
      headerName: "Mandants",
      flex: 1,
      headerClassName: "primaryLight",
      renderCell: (params) => {
        return (
          <strong>
            {params.row.mandants
              ? params.row.mandants.map((mandant) => {
                  if (mandant.nom === null) return "";

                  return mandant.nom + " " + mandant.prenom + ", ";
                })
              : "N/A"}
          </strong>
        );
      },
    },
    {
      field: "statut.nom",
      headerName: "Statut",
      flex: 1,
      headerClassName: "primaryLight",
      renderCell: (params) => {
        return (
          <strong>
            <span
              style={{
                color: _STATUTCOLORS[params.row.statut.nomTechnique],
              }}
            >
              {params.row.statut ? params.row.statut.nom : "N/A"}
            </span>
          </strong>
        );
      },
    },
    {
      field: "typeVente.id",
      headerName: "Type de vente",
      flex: 1,
      headerClassName: "primaryLight",
      renderCell: (params) => {
        return (
          <strong>
            <span>
              {params.row.typeVente ? params.row.typeVente.nom : "N/A"}
            </span>
          </strong>
        );
      },
    },
    {
      field: "exclusif",
      headerName: "Exclusif",
      flex: 1,
      headerClassName: "primaryLight",
      renderCell: (params) => {
        let status =
          params.row.exclusif === null
            ? "Non renseigné"
            : params.row.exclusif
              ? "Oui"
              : "Non";

        return <strong>{status}</strong>;
      },
    },
    {
      field: "adresseBien",
      headerName: "Adresse du bien",
      flex: 1,
      headerClassName: "primaryLight",
      renderCell: (params) => {
        return (
          <strong>{params.row.bien ? params.row.bien.adresse : "N/A"}</strong>
        );
      },
    },
    {
      field: "dateCreation",
      headerName: "Date de création",
      flex: 1,
      headerClassName: "primaryLight",
      renderCell: (params) => {
        return (
          <strong>
            {params.row.dateCreation
              ? moment(params.row.dateCreation).format("DD/MM/YYYY")
              : "N/A"}
          </strong>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      headerClassName: "primaryLight",
      renderCell: (params) => (
        <strong>
          <MandatAction
            params={params}
            refreshGrid={refreshGrid}
          ></MandatAction>
        </strong>
      ),
    },
  ];

  // Création d'un mandat
  function createMandatBddAndEdit(agence, mode = "vente") {
    if (agence) {
      requestHelper
        .doRequest(_ACTIONS.POST, "/api/mandats", {
          bien: {},
          type: mode,
          ventilations: [{}, {}],
          agence: "/api/agences/" + agence,
        })
        .then((response) => {
          if (response.status === 201) {
            createToast(_SWAL_ICONS.success, _MESSAGES.mandat_create_success);
            navigate(`/mandat-${mode}/` + response.data["id"]);
          } else {
            createToast(_SWAL_ICONS.error, _MESSAGES.mandat_create_error);
          }
        });
    }
  }

  /** Creation d'un mandant de vente vide **/
  const handleCreateMandatVente = () => {
    if (user.agences.length === 1) {
      createMandatBddAndEdit(user.agences[0].id);
    } else {
      (async () => {
        const { value: agence } = await Swal.fire({
          title: "Veuillez sélectionner une agence",
          input: "select",
          inputOptions: createAgenceOptions(agences),
          inputPlaceholder: "Sélectionner une agence",
          showCancelButton: true,
          cancelButtonText: "Annuler",
        });
        createMandatBddAndEdit(agence);
      })();
    }
  };

  /** Création d'un mandat de recherche */
  const handleCreateMandatRecherche = () => {
    if (user.agences.length === 1) {
      createMandatBddAndEdit(user.agences[0].id, "recherche");
    } else {
      (async () => {
        const { value: agence } = await Swal.fire({
          title: "Veuillez sélectionner une agence",
          input: "select",
          inputOptions: createAgenceOptions(agences),
          inputPlaceholder: "Sélectionner une agence",
          showCancelButton: true,
          cancelButtonText: "Annuler",
        });
        createMandatBddAndEdit(agence, "recherche");
      })();
    }
  };

  /** Création d'une lettre d'intention d'achat */
  const handleCreateLettreIntention = () => {
    if (user.agences.length === 1) {
      createMandatBddAndEdit(user.agences[0].id, "lettre-intention");
    } else {
      (async () => {
        const { value: agence } = await Swal.fire({
          title: "Veuillez sélectionner une agence",
          input: "select",
          inputOptions: createAgenceOptions(agences),
          inputPlaceholder: "Sélectionner une agence",
          showCancelButton: true,
          cancelButtonText: "Annuler",
        });
        createMandatBddAndEdit(agence, "lettre-intention");
      })();
    }
  };

  /** Création d'une transmission de l'offre d'achat */
  const handleCreateTransmissionOffreAchat = () => {
    if (user.agences.length === 1) {
      createMandatBddAndEdit(user.agences[0].id, "presentation-offre-achat");
    } else {
      (async () => {
        const { value: agence } = await Swal.fire({
          title: "Veuillez sélectionner une agence",
          input: "select",
          inputOptions: createAgenceOptions(agences),
          inputPlaceholder: "Sélectionner une agence",
          showCancelButton: true,
          cancelButtonText: "Annuler",
        });
        createMandatBddAndEdit(agence, "presentation-offre-achat");
      })();
    }
  };

  return (
    <Box sx={_STYLES.mainContainer}>
      <Grid sx={_STYLES.filterFormGrid} container>
        {/** Title */}
        <Grid item xl={3}>
          <Typography
            variant="h5"
            style={{
              padding: "15px",
              fontWeight: "bold",
              color: _COLORS.primary,
            }}
          >
            Recherche mandat
          </Typography>
        </Grid>
        <Grid item xl={9}></Grid>
        {/* Filter Form */}
        <Grid item xl={12}>
          <MandatsFilterForm
            gridRef={gridRef}
            clickedChoice={props.clickedChoice}
            setRowsMandats={setRowsMandats}
            refresh={refresh}
            agences={agences}
            user={user}
          />
        </Grid>
      </Grid>
      {/* Modal */}
      <BasicModal
        showModal={showModal}
        setShowModal={setShowModal}
        title="Création d'une IP isolée"
        content={
          <IpIsoleeForm
            showModal={showModal}
            setShowModal={setShowModal}
          ></IpIsoleeForm>
        }
      />

      {/* Spacer */}
      <Box sx={{ height: "2%" }}></Box>

      {/* Table */}
      <Box sx={_STYLES.secondaryContainer}>
        <Grid sx={{ width: "100%" }} container rowSpacing={2}>
          <Grid item size="auto">
            <Typography
              variant="h5"
              style={{
                padding: "10px",
                fontWeight: "bold",
                color: _COLORS.primary,
              }}
            >
              Liste des mandats
            </Typography>
          </Grid>
          <Grid item size="auto">
            <Button
              sx={{
                padding: "8px 15px",
                margin: "0px 10px",
                ...btn(_COLORS.light, _COLORS.secondary),
              }}
              variant="contained"
              onClick={showModalHandler}
              startIcon={<AddCircleOutlineIcon></AddCircleOutlineIcon>}
            >
              Créer une IP isolée
            </Button>
          </Grid>
          <Grid item size="auto">
            <Button
              sx={{
                padding: "8px 15px",
                margin: "0px 10px",
                ...btn(_COLORS.secondary, _COLORS.success),
              }}
              variant="contained"
              startIcon={<AddCircleOutlineIcon></AddCircleOutlineIcon>}
              onClick={handleCreateMandatVente}
            >
              Créer un mandat de vente
            </Button>
          </Grid>
          <Grid item size="auto">
            <Button
              sx={{
                padding: "8px 15px",
                margin: "0px 10px",
                ...btn(_COLORS.primaryLight, _COLORS.primaryUltraLight),
              }}
              variant="contained"
              startIcon={<AddCircleOutlineIcon></AddCircleOutlineIcon>}
              onClick={handleCreateMandatRecherche}
            >
              Créer un mandat de recherche
            </Button>
          </Grid>
          <Grid item size="auto">
            <Button
              sx={{
                padding: "8px 15px",
                margin: "0px 10px",
                ...btn(_COLORS.raspberry, _COLORS.primaryUltraLight),
              }}
              variant="contained"
              startIcon={<AddCircleOutlineIcon></AddCircleOutlineIcon>}
              onClick={handleCreateLettreIntention}
            >
              Lettre d'intention d'achat
            </Button>
          </Grid>
          <Grid item size="auto">
            <Button
              sx={{
                padding: "8px 15px",
                margin: "0px 10px",
                ...btn(_COLORS.darkPink, _COLORS.primaryUltraLight),
              }}
              variant="contained"
              startIcon={<AddCircleOutlineIcon></AddCircleOutlineIcon>}
              onClick={handleCreateTransmissionOffreAchat}
            >
              Présentation d'une lettre d'intention d'achat
            </Button>
          </Grid>
          {user.profil === "ADMIN" ||
            (user.profil === "SADMIN" && (
              <Grid item size="auto">
                <Button
                  sx={{
                    padding: "8px 15px",
                    margin: "0px 10px",
                    ...btn(_COLORS.success, _COLORS.light),
                  }}
                  variant="contained"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportToExcel}
                >
                  Exporter en Excel
                </Button>
              </Grid>
            ))}
          <Grid item xs={12}>
            <Box sx={_STYLES.dataGridContainer}>
              <DataGrid
                localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                sx={{ height: "500px" }}
                rows={rowsMandats}
                columns={columnsListMandats}
                disableSelectionOnClick={true}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 10,
                      page: 0,
                    },
                  },
                }}
                pageSizeOptions={[10, 25]}
                ref={gridRef}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default ListeMandats;
